import React from "react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { isValidHexCode } from "../utils/isHex";
import CardList from "./cardList";
import {
  isLightMode,
  getUniqueTags,
  filterCards,
  flattenCourses,
} from "./calendarFunctions";

import "./calendar.css";
import CalendarHeader from "./calendarHeader";
import LoadingSpinner from "./loading";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const Calendar = () => {
  const [data, setData] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [activeTags, setActiveTags] = useState([]);

  const { username } = useParams();

  const query = useQuery();
  const bg = query.get("bg");
  const text = query.get("text"); // Replace 'paramName' with your parameter name
  const button = query.get("button"); // Replace 'paramName' with your parameter name
  const ftagsParam = query.get("ftags");
  const featuredTags = (ftagsParam || "")
    .split(",")
    .map((t) => t.trim())
    .filter((t) => t.length);

  useEffect(() => {
    const apiURL = `https://www.viewcy.com/api/o/${username.toLowerCase()}/courses`;

    async function getApiData() {
      axios
        .get(apiURL)
        .then(({ data }) => {
          setData(flattenCourses(data.data));
          setIsLoaded(true);
          // console.log(data.data);
        })
        .catch(() => setIsLoaded(true));
    }

    if (bg !== undefined && isValidHexCode(bg)) {
      let r = parseInt(bg.slice(0, 2), 16);
      let g = parseInt(bg.slice(2, 4), 16);
      let b = parseInt(bg.slice(4), 16);

      document.documentElement.style.setProperty(
        "--bg",
        `rgba(${r},${g},${b},1)`
      );
      document.documentElement.style.setProperty(
        "--transBg",
        `rgba(${r},${g},${b},0)`
      );
    }

    if (text !== undefined && isValidHexCode(text)) {
      document.documentElement.style.setProperty(
        "--text",
        `#${text.toLowerCase()}`
      );
    }

    if (button !== undefined && isValidHexCode(button)) {
      document.documentElement.style.setProperty(
        "--button",
        `#${button.toLowerCase()}`
      );
      document.documentElement.style.setProperty(
        "--buttonHover",
        `#${button.toLowerCase()}aa`
      );
      document
        .querySelector('meta[name="theme-color"]')
        .setAttribute("content", `#${button.toLowerCase()}`);
    }

    if (bg !== undefined && text !== undefined) {
      document.documentElement.style.setProperty(
        "--buttonShade",
        isLightMode(bg, text) ? "rgba(0,0,0,.1)" : "rgba(255,255,255,.1)"
      );
    }

    getApiData();
  }, [username, bg, text, button]);

  const availableTags = useMemo(() => {
    if (featuredTags.length) {
      const allTags = getUniqueTags(data);
      const lowerAll = new Set(allTags.map((t) => t.toLowerCase()));
      return featuredTags.filter((ft) => lowerAll.has(ft.toLowerCase()));
    }
    // No featured tags provided → show none (no fallback to all tags)
    return [];
  }, [data, featuredTags]);
  const filteredCards = useMemo(
    () =>
      filterCards(
        data,
        search,
        new Set(activeTags.map((t) => t.toLowerCase()))
      ),
    [data, search, activeTags]
  );

  const isFiltering = search.trim().length > 0 || activeTags.length > 0;

  function toggleTag(tag) {
    setActiveTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  }

  return (
    <div className="calendar-wrapper">
      {isLoaded ? (
        <div className="calendar-container">
          {!!data.length && (
            <CalendarHeader
              data={data}
              tags={availableTags}
              activeTags={activeTags}
              onToggleTag={toggleTag}
              search={search}
              onSearchChange={setSearch}
            />
          )}
          {!!data.length && (
            <CardList
              cards={filteredCards}
              emptyMessage={
                isFiltering
                  ? search.trim()
                    ? `No events match "${search}"`
                    : "No events match your filters"
                  : undefined
              }
            />
          )}
          {!data.length && <h1>There are no events to display</h1>}
        </div>
      ) : (
        <LoadingSpinner />
      )}
    </div>
  );
};

export default Calendar;
